import type { GameResult } from "./game";
import type { GameReview, MoveReview } from "./review";
import { buildFacts, PIECE_NAME } from "./facts";
import { Chess } from "chess.js";

export function gameOutcome(result: GameResult, playerColor: "w" | "b") {
  return result.winner === null ? "draw" : result.winner === playerColor ? "win" : "loss";
}

export function resultDescription(result: GameResult, playerColor: "w" | "b", opponent: string) {
  if (result.winner === null) {
    const reasons: Record<string, string> = {
      stalemate: "No legal moves, and no check. The game is a draw.",
      "insufficient material": "Neither side has enough pieces left to force checkmate.",
      "threefold repetition": "The same position appeared three times.",
      "fifty-move rule": "Fifty moves passed without a pawn move or capture.",
    };
    return reasons[result.reason] ?? `Draw by ${result.reason}.`;
  }
  const won = result.winner === playerColor;
  if (result.reason === "resignation") return won ? `${opponent} resigned.` : `You resigned against ${opponent}.`;
  if (result.reason === "checkmate") return won ? `You checkmated ${opponent}.` : `${opponent} delivered checkmate.`;
  return `${won ? "You won" : opponent + " won"} by ${result.reason}.`;
}

/** Group only the player's moves; opening and forced moves stay separate. */
export function summarizeReview(review: GameReview, color: "w" | "b") {
  const own = review.moves.filter((move) => move.color === color);
  const count = (...classes: MoveReview["classification"][]) => own.filter((move) => classes.includes(move.classification)).length;
  const strong = count("best", "excellent", "good");
  const opening = count("book"), forced = count("forced"), slips = count("inaccuracy"), mistakes = count("mistake"), blunders = count("blunder");
  const learning = own.filter((move) => ["inaccuracy", "mistake", "blunder"].includes(move.classification));
  const biggest = [...learning].sort((a, b) => (b.moverWinBefore - b.moverWinAfter) - (a.moverWinBefore - a.moverWinAfter))[0] ?? null;
  return { total: own.length, strong, opening, forced, slips, mistakes, blunders, biggest, accuracy: own.length ? review.accuracy[color] : null };
}

export const QUALITY_LABELS = {
  best: "Best move", excellent: "Very strong", good: "Solid move", book: "Opening move", forced: "Only legal move",
  inaccuracy: "Small slip", mistake: "Mistake", blunder: "Major mistake",
} satisfies Record<MoveReview["classification"], string>;

export const QUALITY_HELP = {
  best: "The engine’s first choice.", excellent: "Kept almost all of your advantage.", good: "Kept your position steady.",
  book: "Part of a known opening.", forced: "There was only one legal move.",
  inaccuracy: "A slightly stronger move was available.", mistake: "Gave up a meaningful advantage.", blunder: "Caused a large drop in your position’s strength.",
} satisfies Record<MoveReview["classification"], string>;

export function advantageLabel(value: number) {
  if (value > 65) return "You had the advantage";
  if (value > 55) return "You had a slight edge";
  if (value >= 45) return "The position was balanced";
  if (value >= 35) return "Your opponent had a slight edge";
  return "Your opponent had the advantage";
}

/** Short, grounded lessons; tactical claims come from verified legal positions. */
export function lessonForMove(move: MoveReview, playerColor: "w" | "b" = move.color) {
  const facts = buildFacts({ fen: move.fenBefore, moveSan: move.san, engine: { before: { ...move.before, pv: move.before.pv.length ? move.before.pv : move.before.best ? [move.before.best] : [] }, after: move.after } });
  const suggested = facts.engine?.bestMoveSan;
  const weaker = ["inaccuracy", "mistake", "blunder"].includes(move.classification);
  const isPlayer = move.color === playerColor;
  const describe = (san: string) => {
    const played = new Chess(move.fenBefore).move(san);
    if (played.isKingsideCastle()) return "Castle kingside";
    if (played.isQueensideCastle()) return "Castle queenside";
    return `${PIECE_NAME[played.piece]} to ${played.to}${played.promotion ? `, promoting to a ${PIECE_NAME[played.promotion]}` : ""}`;
  };
  const capture = facts.freeCaptures[0];
  let explanation = isPlayer ? QUALITY_HELP[move.classification] : QUALITY_HELP[move.classification].replaceAll("your", "their");
  let takeaway = "Check your opponent’s threats, then compare your candidate moves.";
  if (facts.move?.mate) {
    explanation = `${isPlayer ? "Your" : "Your opponent’s"} move delivered checkmate. The king has no escape.`;
    takeaway = "Look for checks when the opposing king has few safe squares.";
  } else if (weaker && capture) {
    explanation = `After ${move.san}, ${isPlayer ? "your opponent" : "you"} can play ${capture.san}, winning a ${capture.gains}.`;
    takeaway = isPlayer ? "Before moving, check which of your pieces your opponent can capture." : "After your opponent moves, look for pieces you can capture safely.";
  } else if (weaker) {
    explanation = `${move.san} gave ${isPlayer ? "your opponent" : "you"} a stronger position.${suggested ? ` The engine finds ${suggested} a better option${isPlayer ? "" : " for your opponent"}.` : ""}`;
    takeaway = "Compare the suggested line with your move and watch how your opponent can respond.";
  } else if (move.classification === "book") {
    explanation = "This move follows a known opening. It is counted separately from the engine’s strong-move categories.";
    takeaway = "Keep developing your pieces and give your king a safe home.";
  } else if (move.classification === "forced") {
    takeaway = "There was no alternative here. Focus your practice on a position with more choices.";
  } else if (facts.engine?.playedWasBest) {
    explanation = `${isPlayer ? "You found" : "Your opponent found"} the engine’s first choice. This is an idea worth understanding.`;
    takeaway = "Step through the follow-up to see how the idea continues.";
  }
  return { played: describe(move.san), suggested, suggestion: suggested ? describe(suggested) : null, explanation, takeaway };
}
